import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Mail,
  BadgeDollarSign,
  Copy,
  Download,
  ExternalLink,
  Image as ImageIcon,
  Send,
  Share2,
  Upload,
  X,
} from 'lucide-react';
import {
  adminCreateOrder,
  adminDeleteOrder,
  adminGetOrder,
  adminGetEmailTemplates,
  adminMarkOrderPaid,
  adminSendOrderEmail,
  adminUpdateOrder,
  describeApiError,
  getPaymentMethods,
  getPublicSettings,
  uploadImage,
} from '../../services/api';
import SendInvoiceModal from '../../components/admin/SendInvoiceModal';
import InvoiceDocument, { invoiceBusiness } from '../../components/InvoiceDocument';
import Modal from '../../components/Modal';
import { useConfirm } from '../../hooks/useConfirm';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import RestoredDraftBanner from '../../components/admin/RestoredDraftBanner';
import EmailLog from '../../components/admin/EmailLog';
import OrderProgress from '../../components/admin/OrderProgress';
import type {
  Order,
  OrderCreatePayload,
  OrderFulfillment,
  OrderLineInput,
  OrderStatus,
  OrderUpdatePayload,
  PaymentMethodOption,
} from '../../types';
import { CARRIERS, normalizeCarrier, trackingUrl } from '../../utils/carriers';
import { emailError, formatPhone, normalizeEmail, phoneError, smsHref } from '../../utils/contact';
import { formatCents } from '../../utils/money';
import { canShare, shareLink } from '../../utils/share';
import {
  canShareImage,
  copyImage,
  downloadImage,
  renderInvoiceImage,
  shareImage,
} from '../../utils/invoiceImage';
import OrderLinesEditor from '../../components/admin/OrderLinesEditor';
import { STATUS_LABEL, STATUS_ORDER } from './orderUtils';
import { statusLabel } from '../../utils/orderProgress';
import MoneyInput from '../../components/admin/MoneyInput';

type Mode = 'new' | 'edit';

const FULFILLMENT_OPTIONS: { value: OrderFulfillment; label: string }[] = [
  { value: 'pickup', label: 'Pickup' },
  { value: 'dropoff', label: 'Drop-off' },
  { value: 'shipping', label: 'Shipping' },
  // Nothing about fulfillment is printed on the invoice.
  { value: 'none', label: 'None' },
];

interface DraftState {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  fulfillment: OrderFulfillment;
  shipping_line1: string;
  shipping_line2: string;
  shipping_city: string;
  shipping_state: string;
  shipping_postal_code: string;
  shipping_country: string;
  due_date: string;
  internal_notes: string;
  status: OrderStatus;
  lines: OrderLineInput[];
  shipping_cents: number;
  tax_cents: number;
  discount_cents: number;
  tracking_number: string;
  carrier: string;
  payment_method: string;
  description: string;
  tax_rate_percent: string;
  invoice_date: string;
  invoice_sample_image_url: string;
}

const emptyDraft = (): DraftState => ({
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  fulfillment: 'pickup',
  shipping_line1: '',
  shipping_line2: '',
  shipping_city: '',
  shipping_state: '',
  shipping_postal_code: '',
  shipping_country: 'US',
  due_date: '',
  internal_notes: '',
  status: 'created',
  lines: [],
  shipping_cents: 0,
  tax_cents: 0,
  discount_cents: 0,
  tracking_number: '',
  carrier: '',
  payment_method: '',
  description: '',
  tax_rate_percent: '',
  invoice_date: '',
  invoice_sample_image_url: '',
});

const fromOrder = (o: Order): DraftState => ({
  customer_name: o.customer_name,
  // Optional on an order taken in person; the field is still a controlled
  // input, so it needs a string either way.
  customer_email: o.customer_email ?? '',
  customer_phone: o.customer_phone ?? '',
  fulfillment: o.fulfillment,
  shipping_line1: o.shipping_address?.line1 ?? '',
  shipping_line2: o.shipping_address?.line2 ?? '',
  shipping_city: o.shipping_address?.city ?? '',
  shipping_state: o.shipping_address?.state ?? '',
  shipping_postal_code: o.shipping_address?.postal_code ?? '',
  shipping_country: o.shipping_address?.country ?? 'US',
  due_date: o.due_date ?? '',
  internal_notes: o.internal_notes ?? '',
  status: o.status,
  lines: o.lines.map(l => ({
    product_id: l.product_id,
    product_name: l.product_name,
    description: l.description,
    qty: l.qty,
    unit_price_cents: l.unit_price_cents,
    item_source: l.item_source,
    personalization: l.personalization,
    notes: l.notes,
  })),
  shipping_cents: o.shipping_cents,
  tax_cents: o.tax_cents,
  discount_cents: o.discount_cents,
  tracking_number: o.tracking_number ?? '',
  // Carrier used to be free text and the backend now only accepts the
  // known codes, so anything older is folded onto one before it can be
  // saved back and rejected.
  carrier: normalizeCarrier(o.carrier),
  payment_method: o.payment_method ?? '',
  description: o.description ?? '',
  tax_rate_percent: o.tax_rate_percent != null ? String(o.tax_rate_percent) : '',
  invoice_date: o.invoice_date ?? '',
  invoice_sample_image_url: o.invoice_sample_image_url ?? '',
});

const buildPayload = (d: DraftState): OrderCreatePayload => {
  const hasShipping =
    d.fulfillment === 'shipping' &&
    (d.shipping_line1 || d.shipping_city || d.shipping_state || d.shipping_postal_code);
  return {
    customer_name: d.customer_name.trim(),
    customer_email: d.customer_email.trim(),
    customer_phone: d.customer_phone || undefined,
    fulfillment: d.fulfillment,
    shipping_address: hasShipping
      ? {
          line1: d.shipping_line1,
          line2: d.shipping_line2 || undefined,
          city: d.shipping_city,
          state: d.shipping_state,
          postal_code: d.shipping_postal_code,
          country: d.shipping_country || 'US',
        }
      : undefined,
    lines: d.lines,
    shipping_cents: d.shipping_cents,
    tax_cents: d.tax_cents,
    discount_cents: d.discount_cents,
    internal_notes: d.internal_notes || undefined,
    due_date: d.due_date || undefined,
    payment_method: d.payment_method || undefined,
    description: d.description || undefined,
    // Blank means "no rate set"; 0 is a real choice (tax-exempt).
    tax_rate_percent: d.tax_rate_percent === '' ? undefined : Number(d.tax_rate_percent),
    invoice_date: d.invoice_date || undefined,
    // '' clears an existing proof; undefined leaves it untouched.
    invoice_sample_image_url: d.invoice_sample_image_url,
    status: d.status,
  };
};

/**
 * JSON.stringify with object keys sorted, so two structurally equal payloads
 * always produce the same string regardless of the order their keys were
 * built in. `undefined` members are dropped, matching stringify's behaviour.
 */
const stableStringify = (value: unknown): string =>
  JSON.stringify(value, (_key, val) =>
    val && typeof val === 'object' && !Array.isArray(val)
      ? Object.keys(val)
          .sort()
          .reduce<Record<string, unknown>>((acc, k) => {
            acc[k] = (val as Record<string, unknown>)[k];
            return acc;
          }, {})
      : val
  );

const OrderEditor: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const mode: Mode = orderId ? 'edit' : 'new';

  const [draft, setDraft] = useState<DraftState>(emptyDraft());
  const [order, setOrder] = useState<Order | undefined>();
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [savedHint, setSavedHint] = useState<string | undefined>();
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [payingOpen, setPayingOpen] = useState(false);
  const [paidMethod, setPaidMethod] = useState('');
  const [methodOptions, setMethodOptions] = useState<PaymentMethodOption[]>([]);
  const { confirm, confirmDialog } = useConfirm();
  const proofRef = useRef<HTMLInputElement | null>(null);
  // Scrolled to when an invoice is built without sending, since texting the
  // link is the whole reason that button was pressed.
  const linkRef = useRef<HTMLDivElement | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('invoice');
  const [emailTemplates, setEmailTemplates] = useState<{ value: string; label: string }[]>([]);
  // Only for the wording of a shared message — the shop's own name reads
  // better in a text than a bare URL does.
  const [businessName, setBusinessName] = useState('Bleums Creations');
  // The whole public settings map, for the footer of the invoice image.
  const [settings, setSettings] = useState<Record<string, string>>({});
  // An offscreen copy of the invoice, drawn at full width, that the image
  // is taken from.
  const imageRef = useRef<HTMLDivElement | null>(null);
  const imageCache = useRef<{ key?: unknown[]; blob?: Promise<Blob> }>({});
  const [imageBusy, setImageBusy] = useState(false);
  // Set when an invoice is built without sending, so the effect below can
  // scroll once the link has actually rendered.
  const [justBuiltLink, setJustBuiltLink] = useState(false);

  // Unsaved edits survive a session expiry, which bounces to login with a
  // hard navigation and takes every bit of in-memory state with it.
  const { restoredAt, commit, discard } = useDraftAutosave<DraftState>({
    key: `order:${orderId ?? 'new'}`,
    value: draft,
    ready: !loading,
    onRestore: setDraft,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (mode !== 'edit' || !orderId) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const o = await adminGetOrder(orderId);
        if (cancelled) return;
        setOrder(o);
        setDraft(fromOrder(o));
      } catch (e: any) {
        if (!cancelled) setError(describeApiError(e, 'Failed to load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(mode === 'edit');
    load();
    return () => {
      cancelled = true;
    };
  }, [mode, orderId]);

  // For the mark-paid dialog. Non-fatal: without them it just offers
  // "not recorded", which is what the prompt's empty answer used to mean.
  useEffect(() => {
    getPaymentMethods().then(setMethodOptions).catch(() => undefined);
    adminGetEmailTemplates().then(setEmailTemplates).catch(() => undefined);
    getPublicSettings()
      .then(s => {
        setSettings(s ?? {});
        if (s?.business_name) setBusinessName(s.business_name);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!justBuiltLink || !linkRef.current) return;
    // The link sits well down a long page, and texting it is the whole
    // reason the button was pressed — so bring it to her rather than
    // leaving her to scroll for what she just made.
    linkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setJustBuiltLink(false);
  }, [justBuiltLink]);

  /**
   * The invoice image, drawn once per version of the order.
   *
   * Kept as a promise so the copy button can hand it to the clipboard
   * straight from the click (see copyImage). A failed render is dropped
   * so the next press tries again rather than failing forever.
   */
  const invoiceImage = (): Promise<Blob> => {
    const key = [order, settings, methodOptions];
    const cached = imageCache.current;
    const fresh = cached.key && key.every((v, i) => v === cached.key![i]);
    if (!fresh || !cached.blob) {
      if (!imageRef.current) return Promise.reject(new Error('Invoice not ready'));
      const blob = renderInvoiceImage(imageRef.current);
      imageCache.current = { key, blob };
      blob.catch(e => {
        // The buttons show a plain message; this is what says why.
        console.error('Invoice image failed', e);
        if (imageCache.current.blob === blob) imageCache.current = {};
      });
    }
    return imageCache.current.blob!;
  };

  // Drawn ahead of time. A phone only opens the share sheet straight from
  // a tap, and drawing the image first can take long enough that it no
  // longer counts as one.
  useEffect(() => {
    if (!order?.invoice_token) return;
    const t = window.setTimeout(() => invoiceImage().catch(() => undefined), 800);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, settings, methodOptions]);

  const onPickProof = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingProof(true);
    setError(undefined);
    try {
      const { s3_url } = await uploadImage(files[0], 'product');
      set('invoice_sample_image_url', s3_url);
    } catch (e: any) {
      setError(describeApiError(e, 'Upload failed'));
    } finally {
      setUploadingProof(false);
      if (proofRef.current) proofRef.current.value = '';
    }
  };

  const flash = (msg: string) => {
    setSavedHint(msg);
    window.setTimeout(() => setSavedHint(undefined), 2000);
  };

  const subtotal = draft.lines.reduce(
    (s, l) => s + (l.qty || 0) * (l.unit_price_cents || 0),
    0
  );
  // Mirrors _order_totals() on the server: rate applies to the discounted
  // subtotal, shipping excluded.
  const rate = draft.tax_rate_percent === '' ? 0 : Number(draft.tax_rate_percent) || 0;
  const estimatedTax = Math.round((Math.max(0, subtotal - draft.discount_cents) * rate) / 100);
  const total = Math.max(
    0,
    subtotal + draft.shipping_cents + estimatedTax - draft.discount_cents
  );
  // Shown so the admin can check the link works before the customer gets it.
  const trackUrl = trackingUrl(draft.carrier, draft.tracking_number);

  // Invoices are built from the *saved* order, not the draft — so unsaved
  // edits must be flushed first or the customer gets an invoice showing
  // stale lines and totals.
  //
  // Compared with sorted keys: plain JSON.stringify is key-order sensitive,
  // so a line added via the editor compared unequal to the same line rebuilt
  // by fromOrder purely because the keys came out in a different order,
  // which left the order permanently "dirty".
  const isDirty = !!order && stableStringify(buildPayload(draft)) !== stableStringify(buildPayload(fromOrder(order)));
  const savedLineCount = order?.lines.length ?? 0;

  const invoiceBlockedReason = !order
    ? 'Create the order before sending an invoice.'
    : savedLineCount === 0
    ? 'Add at least one line item, then save.'
    : isDirty
    ? 'You have unsaved changes — save them first so the invoice matches.'
    : undefined;

  // An order can have no email address at all — one taken in person, where
  // a phone number is all there is. That stops nothing except emailing: the
  // invoice is still built, and its link is still there to be texted over.
  const hasEmail = Boolean(draft.customer_email.trim());

  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      const payload = buildPayload(draft);
      if (mode === 'new') {
        const created = await adminCreateOrder(payload);
        navigate(`/admin/orders/${created.order_id}`, { replace: true });
        flash('Created');
      } else if (orderId) {
        const patch: OrderUpdatePayload = {
          ...payload,
          tracking_number: draft.tracking_number || undefined,
          carrier: draft.carrier || undefined,
        };
        const updated = await adminUpdateOrder(orderId, patch);
        setOrder(updated);
        // Re-seed the draft from what the server actually stored. Without
        // this the draft keeps pre-save values while `order` holds the
        // server's normalised ones (computed tax, defaulted tax rate,
        // line_total_cents), so isDirty would stay true forever and the
        // invoice button would never unlock.
        setDraft(fromOrder(updated));
        commit(fromOrder(updated));
        flash('Saved');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const transition = async (next: OrderStatus) => {
    if (!orderId) return;
    setSaving(true);
    try {
      const updated = await adminUpdateOrder(orderId, { status: next });
      setOrder(updated);
      setDraft(fromOrder(updated));
      commit(fromOrder(updated));
      flash(statusLabel(STATUS_LABEL, next, updated.fulfillment));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleInvoiceSent = (updated: Order, emailed: boolean) => {
    setOrder(updated);
    setDraft(fromOrder(updated));
    commit(fromOrder(updated));
    if (!emailed) {
      flash('Invoice ready to send');
      setJustBuiltLink(true);
      return;
    }
    flash(updated.payment_link_url ? 'Invoice sent with online link' : 'Invoice sent');
  };

  const openMarkPaid = () => {
    setPaidMethod(draft.payment_method || '');
    setPayingOpen(true);
  };

  // The method used to be typed into a browser prompt, which meant
  // remembering the exact spelling of a key the invoice already knows.
  const markPaid = async (method: string) => {
    if (!orderId) return;
    setPayingOpen(false);
    setSaving(true);
    try {
      const updated = await adminMarkOrderPaid(orderId, method || undefined);
      setOrder(updated);
      setDraft(fromOrder(updated));
      commit(fromOrder(updated));
      flash('Marked paid');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    if (!orderId) return;
    setEmailOpen(false);
    setSaving(true);
    setError(undefined);
    try {
      const result = await adminSendOrderEmail(orderId, emailTemplate);
      flash(`Sent to ${result.to?.[0] ?? draft.customer_email}`);
      // Pull the order back so the email log shows what just went out.
      // This send is synchronous, so the entry is already written.
      adminGetOrder(orderId).then(setOrder).catch(() => undefined);
    } catch (e: any) {
      setError(describeApiError(e, 'Could not send that email'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!orderId) return;
    const ok = await confirm({
      title: 'Delete this order?',
      body: 'The order and its invoice are removed for good. This cannot be undone.',
      confirmLabel: 'Delete order',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await adminDeleteOrder(orderId);
      navigate('/admin/orders');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Delete failed');
    }
  };

  if (loading) return <div className="text-text-light">Loading…</div>;

  const set = <K extends keyof DraftState>(k: K, v: DraftState[K]) =>
    setDraft(d => ({ ...d, [k]: v }));

  const invoiceLabel = order?.invoice_number
    ? `Invoice #${String(order.invoice_number).padStart(3, '0')}`
    : 'Your invoice';
  const imageFilename = order?.invoice_number
    ? `bleums-invoice-${String(order.invoice_number).padStart(3, '0')}.png`
    : 'bleums-invoice.png';

  const onTextImage = async () => {
    setError(undefined);
    setImageBusy(true);
    try {
      const outcome = await shareImage(await invoiceImage(), imageFilename);
      if (outcome === 'shared') flash('Shared');
    } catch {
      setError('Could not share the image — try Copy image or Save image instead.');
    } finally {
      setImageBusy(false);
    }
  };

  const onCopyImage = async () => {
    setError(undefined);
    setImageBusy(true);
    const blob = invoiceImage();
    try {
      await copyImage(blob);
      flash('Invoice image copied');
    } catch {
      // Some browsers won't put images on the clipboard; saving the file
      // gets her the same picture by another door.
      try {
        downloadImage(await blob, imageFilename);
        flash("Couldn't copy, so the image was saved instead");
      } catch {
        setError('Could not make the invoice image.');
      }
    } finally {
      setImageBusy(false);
    }
  };

  const onSaveImage = async () => {
    setError(undefined);
    setImageBusy(true);
    try {
      downloadImage(await invoiceImage(), imageFilename);
    } catch {
      setError('Could not make the invoice image.');
    } finally {
      setImageBusy(false);
    }
  };

  // Token-addressed so the customer's email stays out of the URL.
  const customerUrl = order?.invoice_token
    ? `${window.location.origin}/invoice/${order.invoice_token}`
    : '';

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Stuck to the top so Save is reachable from anywhere in a form
          this long, rather than only from the very top of it. Below `md`
          it sits under the admin bar, which is itself sticky at h-14. The
          negative margins let its background span the padding of <main>,
          so nothing shows through in the gutters as content passes under. */}
      <div
        className="sticky top-14 md:top-0 z-20 bg-background border-b border-background-dark
                   -mx-4 px-4 sm:-mx-6 sm:px-6 py-3
                   flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
      >
        <h1 className="text-2xl font-semibold">
          {mode === 'new' ? 'New order' : `Order ${order?.order_number ?? ''}`}
        </h1>
        <div className="flex items-center flex-wrap gap-2">
          {savedHint && <span className="text-sm text-green-700">{savedHint}</span>}
          {mode === 'edit' && (
            <button
              type="button"
              onClick={remove}
              className="border border-red-200 text-red-600 rounded px-3 py-2 text-sm hover:bg-red-50"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={save}
            // Blank email is allowed; a malformed one would 422 on save.
            disabled={
              saving ||
              !draft.customer_name ||
              Boolean(draft.customer_email && emailError(draft.customer_email))
            }
            className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : mode === 'new' ? 'Create order' : 'Save'}
          </button>
        </div>
      </div>

      {mode === 'edit' && order && <OrderProgress order={order} paymentMethods={methodOptions} />}

      <RestoredDraftBanner
        savedAt={restoredAt}
        onDiscard={() =>
          discard(order ? fromOrder(order) : emptyDraft())
        }
        discardsTo={order ? 'the saved order' : 'a blank form'}
      />

      {error && <div className="text-sm text-red-600">{error}</div>}

      {mode === 'edit' && order && (
        <section className="bg-white border border-background-dark rounded p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-text-light">Current status</div>
              <div className="text-lg font-medium">{statusLabel(STATUS_LABEL, order.status, order.fulfillment)}</div>
              {order.payment_link_url && (
                <a
                  href={order.payment_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  Stripe payment link
                </a>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                type="button"
                onClick={() => setInvoiceModalOpen(true)}
                disabled={saving || Boolean(invoiceBlockedReason)}
                title={
                  invoiceBlockedReason ??
                  'Build this invoice — you choose whether to email it'
                }
                className="border border-background-dark rounded px-3 py-2 text-sm inline-flex items-center gap-1 hover:border-primary disabled:opacity-50"
              >
                <Mail size={14} /> Create invoice
              </button>
              <button
                type="button"
                onClick={openMarkPaid}
                disabled={saving}
                className="border border-background-dark rounded px-3 py-2 text-sm inline-flex items-center gap-1 hover:border-primary disabled:opacity-50"
              >
                <BadgeDollarSign size={14} /> Mark paid
              </button>
              {/* Any of the customer templates, on demand — for resending a
                  lost invoice or nudging someone without a status change. */}
              <button
                type="button"
                onClick={() => setEmailOpen(true)}
                disabled={saving || !hasEmail}
                title={
                  hasEmail
                    ? 'Pick an email to send this customer'
                    : "No email address on this order — add one above, or text them the invoice link"
                }
                className="border border-background-dark rounded px-3 py-2 text-sm inline-flex items-center gap-1 hover:border-primary disabled:opacity-50"
              >
                <Send size={14} /> Send email
              </button>
              <select
                value={order.status}
                onChange={e => {
                  const next = e.target.value as OrderStatus;
                  // Paid goes through the same dialog as the button, so the
                  // method is asked for whichever way the order gets there.
                  // The select stays put until the dialog is confirmed.
                  if (next === 'paid') openMarkPaid();
                  else transition(next);
                }}
                className="border border-background-dark rounded px-3 py-2 text-sm bg-white"
              >
                {STATUS_ORDER.map(s => (
                  <option key={s} value={s}>
                    {statusLabel(STATUS_LABEL, s, order.fulfillment)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {order.invoice_number && (
            <div className="text-xs text-text-light">
              Invoice #{String(order.invoice_number).padStart(3, '0')}
              {order.invoice_date ? ` · ${order.invoice_date}` : ''}
            </div>
          )}
          {invoiceBlockedReason && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Can't send an invoice yet: {invoiceBlockedReason}
            </div>
          )}
          {!invoiceBlockedReason && !hasEmail && (
            <div className="text-xs text-text-light bg-background/60 border border-background-dark rounded px-3 py-2">
              No email address on this order. You can still build the invoice
              and copy its link to text over — open <strong>Create invoice</strong>{' '}
              and choose <strong>Create link only</strong>.
            </div>
          )}
          {/* The same link the invoice email points at. Shareable on its
              own so it can be texted to a customer who'd rather have it
              that way, or opened on a phone to screenshot. */}
          {customerUrl && (
            <div ref={linkRef} className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                {/* On a phone this opens Messages (or whatever else is
                    installed) with the link already in it. Browsers with no
                    share sheet fall back to the clipboard, so the button is
                    worth showing either way — it just says so. */}
                <button
                  type="button"
                  onClick={async () => {
                    const outcome = await shareLink({
                      title: invoiceLabel,
                      text: `${invoiceLabel} from ${businessName}`,
                      url: customerUrl,
                    });
                    if (outcome === 'shared') flash('Shared');
                    if (outcome === 'copied') flash('Invoice link copied');
                    if (outcome === 'failed') {
                      setError('Could not share — select the link below instead.');
                    }
                  }}
                  className="inline-flex items-center gap-1 bg-primary text-white rounded px-3 py-1.5 text-xs font-medium"
                >
                  <Share2 size={13} /> {canShare() ? 'Text as link' : 'Copy link + message'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(customerUrl)
                      .then(() => flash('Invoice link copied'))
                      .catch(() => setError('Could not copy — select the link below instead.'));
                  }}
                  className="inline-flex items-center gap-1 border border-background-dark rounded px-3 py-1.5 text-xs hover:border-primary"
                >
                  <Copy size={13} /> Copy link only
                </button>
                <a
                  className="inline-flex items-center gap-1 border border-background-dark rounded px-3 py-1.5 text-xs hover:border-primary"
                  href={customerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={13} /> Open
                </a>
                <span className="text-xs text-text-light">
                  Works whether or not they have an email address
                </span>
              </div>
              {/* The same invoice as a picture, for a customer who'd
                  rather not tap a link. Taken from the saved order, so
                  save any edits first. */}
              <div className="flex items-center gap-2 flex-wrap">
                {canShareImage() && (
                  <button
                    type="button"
                    onClick={onTextImage}
                    disabled={imageBusy}
                    className="inline-flex items-center gap-1 bg-primary text-white rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                  >
                    <ImageIcon size={13} /> Text as image
                  </button>
                )}
                <button
                  type="button"
                  onClick={onCopyImage}
                  disabled={imageBusy}
                  className="inline-flex items-center gap-1 border border-background-dark rounded px-3 py-1.5 text-xs hover:border-primary disabled:opacity-50"
                >
                  <Copy size={13} /> Copy image
                </button>
                <button
                  type="button"
                  onClick={onSaveImage}
                  disabled={imageBusy}
                  className="inline-flex items-center gap-1 border border-background-dark rounded px-3 py-1.5 text-xs hover:border-primary disabled:opacity-50"
                >
                  <Download size={13} /> Save image
                </button>
                {imageBusy && <span className="text-xs text-text-light">Drawing invoice…</span>}
              </div>
              <div className="text-xs text-text-light break-all">{customerUrl}</div>
            </div>
          )}
        </section>
      )}

      {mode === 'new' && (
        <section className="bg-background/50 border border-background-dark rounded p-4 text-sm text-text-light">
          Add the customer and line items, then <strong>Create order</strong>. Sending the
          invoice, marking it paid, and status changes become available once it exists.
        </section>
      )}

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <div className="text-sm font-medium">Customer</div>
        {/* items-start stops a bare input stretching to whatever the tallest
            cell in its row happens to be, which is what made the name box
            taller than the email box beside it. Every field carries a label
            so the inputs line up across both columns, and the fields with a
            helper line underneath reserve its height whether or not it's
            showing -- otherwise the row below jumps as soon as an address
            becomes valid. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3 items-start">
          <div>
            <label className="block text-[11px] text-text-light mb-1">Name</label>
            <input
              value={draft.customer_name}
              onChange={e => set('customer_name', e.target.value)}
              placeholder="Customer name"
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-text-light mb-1">
              Email (optional)
            </label>
            {/* Tidied when the field is left, never while it's being typed
                in -- reformatting mid-keystroke is what made the price
                fields unusable. */}
            <input
              type="email"
              value={draft.customer_email}
              onChange={e => set('customer_email', e.target.value)}
              onBlur={() => set('customer_email', normalizeEmail(draft.customer_email))}
              placeholder="name@example.com"
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
            <div className="h-4 mt-1 text-xs leading-4">
              {/* Nothing at all without an address -- there's no one to
                  write to, so offering to is noise. A typo is worth saying
                  before the save rather than after, since the server
                  rejects it either way. */}
              {draft.customer_email &&
                (emailError(draft.customer_email) ? (
                  <span className="text-red-600">{emailError(draft.customer_email)}</span>
                ) : (
                  <a
                    href={`mailto:${draft.customer_email}`}
                    className="text-primary hover:underline"
                  >
                    Email this customer
                  </a>
                ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-text-light mb-1">
              Phone (optional)
            </label>
            <input
              type="tel"
              value={draft.customer_phone}
              onChange={e => set('customer_phone', e.target.value)}
              onBlur={() => set('customer_phone', formatPhone(draft.customer_phone))}
              placeholder="(555) 123-4567"
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
            <div className="h-4 mt-1 text-xs leading-4">
              {draft.customer_phone &&
                (phoneError(draft.customer_phone, false) ? (
                  <span className="text-red-600">
                    {phoneError(draft.customer_phone, false)}
                  </span>
                ) : (
                  <a href={smsHref(draft.customer_phone)} className="text-primary hover:underline">
                    Text {formatPhone(draft.customer_phone)}
                  </a>
                ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-text-light mb-1">Due date</label>
            <input
              type="date"
              value={draft.due_date}
              onChange={e => set('due_date', e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Fulfillment</div>
          <div className="inline-flex border border-background-dark rounded overflow-hidden">
            {FULFILLMENT_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => set('fulfillment', value)}
                className={`px-3 py-1.5 text-sm ${
                  draft.fulfillment === value ? 'bg-primary text-white' : 'bg-white'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {draft.fulfillment === 'shipping' && (
          <div className="grid grid-cols-2 gap-2">
            <input
              value={draft.shipping_line1}
              onChange={e => set('shipping_line1', e.target.value)}
              placeholder="Address line 1"
              className="col-span-2 border border-background-dark rounded px-3 py-2 text-sm"
            />
            <input
              value={draft.shipping_line2}
              onChange={e => set('shipping_line2', e.target.value)}
              placeholder="Address line 2"
              className="col-span-2 border border-background-dark rounded px-3 py-2 text-sm"
            />
            <input
              value={draft.shipping_city}
              onChange={e => set('shipping_city', e.target.value)}
              placeholder="City"
              className="border border-background-dark rounded px-3 py-2 text-sm"
            />
            <input
              value={draft.shipping_state}
              onChange={e => set('shipping_state', e.target.value)}
              placeholder="State"
              className="border border-background-dark rounded px-3 py-2 text-sm"
            />
            <input
              value={draft.shipping_postal_code}
              onChange={e => set('shipping_postal_code', e.target.value)}
              placeholder="ZIP"
              className="border border-background-dark rounded px-3 py-2 text-sm"
            />
            <input
              value={draft.shipping_country}
              onChange={e => set('shipping_country', e.target.value)}
              placeholder="Country"
              className="border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
        )}
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <OrderLinesEditor lines={draft.lines} onChange={lines => set('lines', lines)} />
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <div className="text-sm font-medium">Totals</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-text-light mb-1">Shipping ($)</label>
            <MoneyInput
              valueCents={draft.shipping_cents}
              onChange={cents => set('shipping_cents', cents)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
            />
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Tax rate (%)</label>
            <input
              type="number"
              step="0.001"
              min={0}
              max={100}
              placeholder="e.g. 7"
              value={draft.tax_rate_percent}
              onChange={e => set('tax_rate_percent', e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
            />
            <p className="text-[11px] text-text-light mt-1">
              {draft.tax_rate_percent === ''
                ? 'Blank = no tax'
                : `${formatCents(estimatedTax)} on this order`}
            </p>
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Discount ($)</label>
            <MoneyInput
              valueCents={draft.discount_cents}
              onChange={cents => set('discount_cents', cents)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
            />
          </div>
        </div>
        <div className="border-t border-background-dark pt-2 text-sm space-y-1">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCents(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Shipping</span>
            <span>{formatCents(draft.shipping_cents)}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax{rate ? ` (${rate}%)` : ''}</span>
            <span>{formatCents(estimatedTax)}</span>
          </div>
          <div className="flex justify-between">
            <span>Discount</span>
            <span>−{formatCents(draft.discount_cents)}</span>
          </div>
          <div className="flex justify-between font-medium text-base pt-1 border-t border-background-dark">
            <span>Total</span>
            <span>{formatCents(total)}</span>
          </div>
        </div>
      </section>

      {mode === 'edit' && draft.fulfillment === 'shipping' && (
        <section className="bg-white border border-background-dark rounded p-4 space-y-3">
          <div className="text-sm font-medium">Shipping tracking</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              value={draft.tracking_number}
              onChange={e => set('tracking_number', e.target.value)}
              placeholder="Tracking number"
              className="border border-background-dark rounded px-3 py-2 text-sm"
            />
            {/* A list rather than free text, so the number becomes a link
                the customer can follow. "Other" still records the number,
                just without one. */}
            <select
              value={draft.carrier}
              onChange={e => set('carrier', e.target.value)}
              className="border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Carrier…</option>
              {CARRIERS.map(c => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {trackUrl ? (
            <a
              href={trackUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Check this tracking link <ExternalLink size={13} />
            </a>
          ) : (
            draft.tracking_number &&
            draft.carrier && (
              <p className="text-xs text-text-light">
                We don't have a tracking page for this carrier, so the customer
                sees the number without a link.
              </p>
            )
          )}

          {/* Saving a tracking number used to email it, as a side effect of
              moving the order to shipped. It doesn't any more, so this says
              where the number goes instead -- otherwise the honest answer to
              "does the customer have this?" is a shrug. */}
          {draft.tracking_number && (
            <div className="text-xs text-text-light bg-background/60 border border-background-dark rounded px-3 py-2 space-y-1">
              <div>
                <strong className="text-text">Save</strong> and this appears on
                the customer's order status page straight away — the page every
                email links to.
              </div>
              <div>
                To tell them it's on its way, press{' '}
                <strong className="text-text">Send email</strong> and pick{' '}
                <strong className="text-text">Your order has shipped</strong>.
                That one includes the tracking number
                {trackUrl ? ' as a link they can follow' : ''}.
              </div>
            </div>
          )}
        </section>
      )}

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <div className="text-sm font-medium">Invoice</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] text-text-light mb-1">
              Invoice date {order?.invoice_date ? '' : '(set when first sent)'}
            </label>
            <input
              type="date"
              value={draft.invoice_date}
              onChange={e => set('invoice_date', e.target.value)}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] text-text-light mb-1">Invoice number</label>
            <div className="border border-background-dark rounded px-3 py-2 text-sm bg-background/50 text-text-light">
              {order?.invoice_number
                ? `#${String(order.invoice_number).padStart(3, '0')}`
                : 'Assigned on send'}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[11px] text-text-light mb-1">
            Proof / mockup image (shown on the invoice)
          </label>
          {draft.invoice_sample_image_url ? (
            <div className="relative inline-block">
              <img
                src={draft.invoice_sample_image_url}
                alt="Proof"
                className="max-h-40 rounded border border-background-dark"
              />
              <button
                type="button"
                onClick={() => set('invoice_sample_image_url', '')}
                className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full p-1"
                aria-label="Remove proof image"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div>
              <input
                ref={proofRef}
                type="file"
                accept="image/*"
                onChange={e => onPickProof(e.target.files)}
                disabled={uploadingProof}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => proofRef.current?.click()}
                disabled={uploadingProof}
                className="inline-flex items-center gap-2 border border-background-dark rounded px-3 py-2 text-sm hover:border-primary disabled:opacity-50"
              >
                <Upload size={14} /> {uploadingProof ? 'Uploading…' : 'Add proof image'}
              </button>
            </div>
          )}
          <p className="text-[11px] text-text-light mt-1">
            Remember to save — the proof is stored on the order.
          </p>
        </div>
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-2">
        <label className="block text-xs text-text-light">
          Invoice note (printed on the invoice, seen by the customer)
        </label>
        <input
          value={draft.description}
          onChange={e => set('description', e.target.value)}
          placeholder="e.g. local pickup, ready in 2 weeks"
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
        <p className="text-[11px] text-text-light">
          Shown in script on the invoice. Defaults to the fulfillment method when empty (nothing, if fulfillment is None).
        </p>
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-2">
        <label className="block text-xs text-text-light">Internal notes (never shown to the customer)</label>
        <textarea
          value={draft.internal_notes}
          onChange={e => set('internal_notes', e.target.value)}
          rows={3}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />
      </section>

      {mode === 'edit' && <EmailLog entries={order?.email_log} fulfillment={order?.fulfillment} />}

      {/* Last on the page: nothing is sent on its own any more, so there
          are no automatic emails left to switch off and this is a note
          rather than a control. */}
      <section className="bg-background/50 border border-background-dark rounded p-4">
        <div className="text-sm font-medium mb-1">Customer emails</div>
        <p className="text-xs text-text-light">
          Nothing is emailed automatically. Changing the status, marking the
          order paid and saving your edits are all silent — the customer only
          hears from you when you press <strong>Email invoice</strong> or{' '}
          <strong>Send email</strong> and pick which one to send.
        </p>
      </section>

      {invoiceModalOpen && order && (
        <SendInvoiceModal
          order={order}
          onClose={() => setInvoiceModalOpen(false)}
          onSent={handleInvoiceSent}
        />
      )}

      {confirmDialog}

      {/* Source for the invoice image: off the screen rather than hidden,
          because a display:none element has nothing to draw. */}
      {order?.invoice_token && (
        <div
          aria-hidden="true"
          className="invoice-wide fixed top-0 -left-[10000px] w-[850px] pointer-events-none"
        >
          <div ref={imageRef}>
            <InvoiceDocument
              order={order}
              paymentMethods={methodOptions}
              business={invoiceBusiness(settings)}
            />
          </div>
        </div>
      )}

      {emailOpen && (
        <Modal
          title="Send an email"
          onClose={() => setEmailOpen(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setEmailOpen(false)}
                className="border border-background-dark rounded px-4 py-2 text-sm hover:border-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={sendEmail}
                className="bg-primary text-white rounded px-4 py-2 text-sm font-medium"
              >
                Send
              </button>
            </>
          }
        >
          <label className="block text-xs text-text-light mb-1">Which email?</label>
          <select
            value={emailTemplate}
            onChange={e => setEmailTemplate(e.target.value)}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white text-text"
          >
            {emailTemplates.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>

          <p className="mt-3">
            Goes to <strong className="text-text">{draft.customer_email}</strong> using
            the order as it is now — save any changes first.
          </p>
        </Modal>
      )}

      {payingOpen && (
        <Modal
          title="Mark this order paid"
          onClose={() => setPayingOpen(false)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setPayingOpen(false)}
                className="border border-background-dark rounded px-4 py-2 text-sm hover:border-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => markPaid(paidMethod)}
                className="bg-primary text-white rounded px-4 py-2 text-sm font-medium"
              >
                Mark paid
              </button>
            </>
          }
        >
          <label className="block text-xs text-text-light mb-1">
            How did they pay?
          </label>
          {/* The shop's own methods, so this can't disagree with what the
              invoice offered — the old prompt took free text and any typo
              became the stored payment method. */}
          <select
            value={paidMethod}
            onChange={e => setPaidMethod(e.target.value)}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white text-text"
          >
            <option value="">Not recorded</option>
            {methodOptions.map(m => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
          <p className="mt-3">
            The order moves to Paid. Nothing is emailed — use <strong>Send email</strong> if
            you'd like to send them a receipt.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default OrderEditor;
